/* eslint-disable max-len */
import { Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Subscription, pairwise, startWith } from 'rxjs';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';

import { AbstractFilterTypeComponent } from '../../../../Common/Filter/models/abstract-filter-type.component';
import {
    KeywordFilterModeEnum,
    MonitoringKeywordFilterModel,
    MonitoringKeywordFilterTypeModel,
    allSearchContexts
} from '../../models/monitoring-keyword-filter.model';
import { MonitoringKeywordGroupModel } from '../../../../Monitor/Shared/models/monitoring-keyword-group.model';
import { ConflictingKeywordGroupValidationModel } from '../../../../Monitor/Shared/models/conflicting-keyword-group-validation.model';
import { MonitoringAdvancedBooleanModel } from '../../models/monitoring-advanced-boolean.model';
import { IncludeExcludeModel } from '../../../../Common/Misc/models/include-exclude-model';
import { SearchContextModel } from '../../models/search-context.model';
import { SearchContextsEnum } from '../../enums/search-contexts.enum';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';

interface KeywordGroupValue {
    keywords: IncludeExcludeModel<string>;
    searchContexts: SearchContextsEnum[];
}
interface ExpertQueryValue {
    filter: string;
    searchContexts: SearchContextsEnum[];
}

type KeywordGroupForm = FormGroup<{
    keywords: FormControl<IncludeExcludeModel<string>>;
    searchContexts: FormControl<SearchContextsEnum[]>;
}>;

type ExpertQueryForm = FormGroup<{
    filter: FormControl<string>;
    searchContexts: FormControl<SearchContextsEnum[]>;
}>;

type KeywordFilterForm = FormGroup<{
    keywordGroups: FormArray<KeywordGroupForm>;
    expertQuery: ExpertQueryForm;
}>;
@Component({
    selector: 'agpr-filter-keyword',
    templateUrl: './filter-keyword.component.html',
    styleUrls: ['./filter-keyword.component.scss'],
    providers: [
        {
            provide: AbstractFilterTypeComponent,
            useExisting: MonitoringKeywordFilterComponent
        }
    ],
    standalone: false
})
export class MonitoringKeywordFilterComponent extends AbstractFilterTypeComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly keywordMode = KeywordFilterModeEnum;
    readonly splitOnPattern: RegExp = /(")?,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)\1 */g;
    readonly searchContextsEnum = SearchContextsEnum;
    readonly keywordChipValidations = new MonitoringKeywordGroupModel().keywordValidations;

    private subscriptions: Subscription[] = [];
    private searchContextsSubscriptions = new Map<FormGroup, Subscription>();

    @Input() declare filter: MonitoringKeywordFilterModel;

    @ViewChild('scrollPanel') scrollPanel: ScrollPanelComponent;
    @ViewChildren('groupItem') groupItems: QueryList<ElementRef<HTMLElement>>;

    value: MonitoringKeywordFilterTypeModel;
    keywordFilter: KeywordFilterForm;

    hasEmptyKeywordGroup: boolean = true;

    constructor(
        private fb: FormBuilder,
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
    ) {
        super();
        this.keywordFilter = this.fb.group({
            keywordGroups: this.fb.array<KeywordGroupForm>(
                [],
                { validators: [this.keywordGroupsMaxLengthValidator(), this.keywordGroupsValidator()]}
            ),
            expertQuery: this.fb.group(
                {
                    filter: this.fb.control<string>(''),
                    searchContexts: this.fb.control<SearchContextsEnum[]>([...allSearchContexts])
                },
                { validators: [this.expertQueryValidator()] }
            )
        }) as KeywordFilterForm;
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.keywordFilter.statusChanges.subscribe((e) => {
                if (this.keywordFilter.invalid) {
                    const errors: Set<string> = new Set();
                    if (this.keywordGroups.errors) {
                        Object.values(this.keywordGroups.errors).forEach(error => errors.add(error.trim()));
                    }
                    this.keywordGroups.controls.forEach(group => {
                        if (group.get('keywords')?.errors) {
                            Object.values(group.get('keywords')?.errors).forEach(error => errors.add(error.trim()));
                        }
                    });
                    if (this.expertQuery.errors) {
                        Object.values(this.expertQuery.errors).forEach(error => errors.add(error.trim()));
                    }
                    this.filter.errors = Array.from(errors);
                } else {
                    this.filter.errors = [];
                }
            }),
            this.keywordGroups.valueChanges.subscribe((keywordGroupsValue) => {
                const keywordGroupModels: MonitoringKeywordGroupModel[] = keywordGroupsValue.map(this.toMonitoringKeywordGroup);
                this.value.keywordGroups = keywordGroupModels;
                this.hasEmptyKeywordGroup = keywordGroupModels.some(x => !x.isAnyKeywordSet());
                this.filter.setCurrentValue(this.value);
            }),
            this.expertQuery.valueChanges.subscribe((expertQueryValue) => {
                this.value.expertQuery = new MonitoringAdvancedBooleanModel();
                this.value.expertQuery.filter = expertQueryValue.filter;
                this.value.expertQuery.searchContexts = expertQueryValue.searchContexts.map(x => new SearchContextModel(x));
                this.filter.setCurrentValue(this.value);
            }),
            // subscribe to changes to enforce at least one search area selected
            this.expertQuery.get('searchContexts').valueChanges
                .pipe(startWith(this.expertQuery.get('searchContexts').value), pairwise())
                .subscribe(([oldValue, newValue]) => {
                    if (!newValue || newValue.length === 0) {
                        this.expertQuery.get('searchContexts')?.setValue([...oldValue], { emitEvent: false });
                    } else {
                        this.expertQuery.get('searchContexts')?.setValue([...new Set(newValue)], { emitEvent: false });
                    }
                })
        );

        // Refresh the value after setting up the subscriptions
        this.refresh();
    }

    /**
     * Refreshes the current value from the filter
     */
    refresh(): void {
        this.value = this.filter.getCurrentValue();
        const {keywordGroups, expertQuery} = this.value;

        if (expertQuery?.filter) {
            this.setFilterMode(KeywordFilterModeEnum.expert);
            this.expertQuery.setValue({
                filter: expertQuery.filter,
                searchContexts: expertQuery.searchContexts.map(x => x.value)
            });
        } else {
            this.setFilterMode(KeywordFilterModeEnum.standard);
            this.keywordGroups.clear({ emitEvent: false });
            if (keywordGroups?.length) {
                keywordGroups.forEach(keywordGroupModel => this.addKeywordGroup(keywordGroupModel));
            } else {
                this.addKeywordGroup();
            }
        }
    }

    /**
     * Maps a keyword group form value to a MonitoringKeywordGroupModel.
     *
     * @param group Keyword group value containing keywords and search contexts
     * @returns A populated MonitoringKeywordGroupModel
     */
    private toMonitoringKeywordGroup(group: KeywordGroupValue): MonitoringKeywordGroupModel {
        const { keywords, searchContexts } = group;

        const keywordGroup = new MonitoringKeywordGroupModel();
        keywordGroup.or = keywords.include ?? [];
        keywordGroup.and = keywords.includeAll ?? [];
        keywordGroup.not = keywords.exclude ?? [];
        keywordGroup.searchContexts = (searchContexts ?? []).map(x => new SearchContextModel(x));

        return keywordGroup;
    }

    get keywordGroups() {
        return this.keywordFilter.get('keywordGroups') as FormArray<KeywordGroupForm>;
    }

    get expertQuery() {
        return this.keywordFilter.get('expertQuery') as ExpertQueryForm;
    }

    addKeywordGroup(keywordGroupModel?: MonitoringKeywordGroupModel): void {
        const includeExcludeValue = new IncludeExcludeModel<string>();
        let searchContextsValue = [...allSearchContexts];
        if (keywordGroupModel) {
            includeExcludeValue.include = keywordGroupModel.or;
            includeExcludeValue.includeAll = keywordGroupModel.and;
            includeExcludeValue.exclude = keywordGroupModel.not;
            searchContextsValue = keywordGroupModel.searchContexts.map(x => x.value);
        }
        const keywordGroup = this.fb.group(
            {
                keywords: this.fb.control(
                    includeExcludeValue,
                    { validators: [this.keywordsValidator()] }
                ),
                searchContexts: this.fb.control<SearchContextsEnum[]>(searchContextsValue)
            }
        ) as KeywordGroupForm;

        // subscribe to changes to enforce at least one search area selected
        const sub = keywordGroup.get('searchContexts')?.valueChanges
            .pipe(startWith(keywordGroup.get('searchContexts')?.value), pairwise())
            .subscribe(([oldValue, newValue]) => {
                if (!newValue || newValue.length === 0) {
                    keywordGroup.get('searchContexts')?.setValue([...oldValue], { emitEvent: false });
                } else {
                    keywordGroup.get('searchContexts')?.setValue([...new Set(newValue)], { emitEvent: false });
                }
            });

        this.searchContextsSubscriptions.set(keywordGroup, sub);
        this.keywordGroups.push(keywordGroup);

        requestAnimationFrame(() => {
            const last = this.groupItems?.last;
            if (last) {
                this.scrollPanel.scrollIntoView(last);
            }
        });
    }

    deleteKeywordGroup(index: number) {
        const group = this.keywordGroups.at(index) as FormGroup;

        // Before removing, unsubscribe the related `valueChanges` subscription
        const sub = this.searchContextsSubscriptions.get(group);
        if (sub) {
            sub.unsubscribe();
            this.searchContextsSubscriptions.delete(group);
        }

        this.keywordGroups.removeAt(index);
    }

    /**
     * Validator function to ensure that no keyword appears in both the include and exclude lists.
     *
     * Combines `include` and `includeAll` arrays for comparison against the `exclude` array.
     * Optionally performs a case-insensitive check (default: false).
     *
     * @param caseSensitive Whether to treat keywords as case-sensitive (default: false)
     * @returns A ValidatorFn suitable for use with FormControl or FormGroup
     */
    private keywordsValidator(caseSensitive: boolean = false): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const includeExcludeModel = (control as FormControl<IncludeExcludeModel<string>>).value;
            const exclude = includeExcludeModel.exclude || [];
            const include = [...includeExcludeModel.include || [], ...includeExcludeModel.includeAll || []];

            for (const keyword of exclude) {
                let word = keyword;
                if (!caseSensitive) {
                    word = word.toLowerCase();
                }

                if (include.includes(word)) {
                    return { 'keywordsError': this.localizationService.transform('Monitoring.search.keywordConflictMessage') };
                }
            }

            return null;
        };
    }

    /**
     * Validator function to check whether the combined keywords across all keyword groups
     * exceed the maximum allowed length.
     *
     * This validator:
     * 1. Uses `MonitoringAdvancedBooleanModel` to construct the expert boolean query.
     * 2. Checks if the resulting query exceeds the allowed length via `isQueryTooLong()`.
     *
     * @see {@link MonitoringAdvancedBooleanModel.maxLength} for the maximum length rule
     * @returns ValidationErrors object with 'maxLengthError' key if invalid, otherwise null
     */
    private keywordGroupsMaxLengthValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const keywordGroups = (control as FormArray<KeywordGroupForm>).value;
            const keywordGroupModels: MonitoringKeywordGroupModel[] = keywordGroups.map(this.toMonitoringKeywordGroup);

            let invalidQueryLength: boolean = true;
            // @TODO a bit hacky, should provide an isQueryTooLong function in MonitoringKeywordGroupModel
            const query = new MonitoringAdvancedBooleanModel();
            query.mapKeywordGroupQueryToExpertBooleanQuery(
                keywordGroupModels,
                true
            );
            invalidQueryLength = query.isQueryTooLong();

            return invalidQueryLength ? { maxLengthError: this.localizationService.transform('Monitoring.search.standardBooleanSearch.queryTooLong') } : null;
        };
    }

    /**
     * Validator to ensure there are no conflicting keywords across all keyword groups and search contexts.
     *
     * A conflicting keyword occurs when the same string (considering the optional case sensitivity)
     * appears in an AND/OR field and a NOT field within the same search context.
     *
     * @param caseSensitive Whether keyword comparison should be case-sensitive (default: false)
     * @returns ValidationErrors object with key 'keywordGroupsError' if invalid, otherwise null
     */
    private keywordGroupsValidator(caseSensitive: boolean = false): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const keywordGroups = (control as FormArray<KeywordGroupForm>).value;
            const keywordGroupModels: MonitoringKeywordGroupModel[] = keywordGroups.map(this.toMonitoringKeywordGroup);
            const {notMap, andOrMap} = ConflictingKeywordGroupValidationModel.generateMapings(keywordGroupModels, caseSensitive);

            let invalidKeywordGroups = false;

            for (const [word, excludedConflictingKeywordModels] of notMap) {
                let wordKey = word;
                if (!caseSensitive) {
                    wordKey = word.toLowerCase();
                }

                const includedConflictingKeywordModels = andOrMap.get(wordKey);
                if (!includedConflictingKeywordModels) {
                    continue;
                }

                for (const includedConflict of includedConflictingKeywordModels) {
                    for (const excludedConflict of excludedConflictingKeywordModels) {
                        if (includedConflict.isInConflict(excludedConflict)) {
                            invalidKeywordGroups = true;
                            break;
                        }
                    }
                    if (invalidKeywordGroups) {
                        break;
                    }
                }
            }

            return invalidKeywordGroups ? { 'keywordGroupsError': this.localizationService.transform('Monitoring.search.keywordGroupConflictMessage') } : null;
        };
    }

    /**
     *  Validator for the `expertQuery` FormGroup to ensure the boolean query is valid.
     */
    private expertQueryValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const expertQueryValue = control.value;
            if (!expertQueryValue) {
                return null;
            }

            const expertQuery = new MonitoringAdvancedBooleanModel();
            expertQuery.filter = expertQueryValue.filter;
            expertQuery.searchContexts = expertQueryValue.searchContexts.map((x: SearchContextsEnum) => new SearchContextModel(x));

            if (expertQuery.isQueryTooShort()) {
                return { 'expertQueryError': this.localizationService.transform('Monitoring.search.expertBooleanSearch.queryTooShort') };
            };

            if (expertQuery.isQueryTooLong()) {
                return { 'expertQueryError': this.localizationService.transform('Monitoring.search.expertBooleanSearch.queryTooLong', { queryLength: expertQuery.filter.length }) };
            };

            if (expertQuery.isPureNot()) {
                return { 'expertQueryError': this.localizationService.transform('Monitoring.search.expertBooleanSearch.pureNot') };
            };

            if(!expertQuery.queryStructureIsValid()) {
                return { 'expertQueryError': this.localizationService.transform(expertQuery.queryValidationError) };
            };

            return null;
        };
    }

    /**
     * Toggles the filter mode between `expert` and `standard`.
     */
    toggleMode() {
        // moving from expert to standard mode
        const {keywordGroups, expertQuery} = this.value;
        if (this.filter.filterMode === KeywordFilterModeEnum.expert) {
            if (expertQuery.filter.length) {
                this.confirmSwitchToStandardMode();
            } else {
                this.setFilterMode(KeywordFilterModeEnum.standard);
            }
        } else {
            // moving from standard to expert mode
            const filteredKeywordGroups = keywordGroups.filter(x => x.hasAnyKeywords());
            if (filteredKeywordGroups.length) {
                this.confirmSwitchToExpertMode(filteredKeywordGroups);
            } else {
                this.setFilterMode(KeywordFilterModeEnum.expert);
            }
        }
    }

    /**
     * Sets the current filter mode to either `standard` or `expert` and updates the form controls accordingly.
     * @param mode - KeywordFilterModeEnum
     */
    private setFilterMode(mode: KeywordFilterModeEnum) {
        if (mode === KeywordFilterModeEnum.standard) {
            this.expertQuery.setValue({
                filter: '',
                searchContexts: [...allSearchContexts]
            } as ExpertQueryValue);
            this.keywordGroups.enable({ emitEvent: false });
            this.expertQuery.disable({ emitEvent: false });
        } else if (mode === KeywordFilterModeEnum.expert) {
            this.keywordGroups.clear({emitEvent: false});
            this.addKeywordGroup();
            this.keywordGroups.disable({ emitEvent: false });
            this.expertQuery.enable({ emitEvent: false });
        }

        this.filter.setFilterMode(mode);
    }

    /**
     * Warn that an existing Expert Boolean Query will be lost when switching back to Standard mode
     */
    private confirmSwitchToStandardMode() {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                heading: 'Monitoring.ExpertBooleanSearch.SwitchToStandardMode.header',
                messageHtml: 'Monitoring.ExpertBooleanSearch.SwitchToStandardMode.message',
                okLabel: 'Monitoring.ExpertBooleanSearch.SwitchToStandardMode.affirmation'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.setFilterMode(KeywordFilterModeEnum.standard);
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    /**
     * Process handling confirmation that switch to Expert Mode to be done with conversion of existing query
     */
    private confirmSwitchToExpertMode(keywordGroups: MonitoringKeywordGroupModel[]) {
        let isAllKeywordGroupsTheSame = true;
        if (keywordGroups.length > 1) {
            // Compare the first keyword group against all other groups.
            for (let i = 1; i < keywordGroups.length; i++) {
                if (!keywordGroups[0].equals(keywordGroups[i])) {
                    isAllKeywordGroupsTheSame = false;
                    break;
                }
            }
        }

        // Do all Keyword Groups contain the same search criteria - if so we can convert the KG info to EBQ
        if (isAllKeywordGroupsTheSame) {
            const io: ConfirmDialogModel = {
                inputs: {
                    heading: 'Monitoring.ExpertBooleanSearch.SwitchToExpertMode.header',
                    messageHtml: 'Monitoring.ExpertBooleanSearch.SwitchToExpertMode.possible.message',
                    okLabel: 'Monitoring.ExpertBooleanSearch.SwitchToExpertMode.affirmation'
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            const expertQueryModel = new MonitoringAdvancedBooleanModel();
                            this.setFilterMode(KeywordFilterModeEnum.expert);
                            expertQueryModel.mapKeywordGroupQueryToExpertBooleanQuery(keywordGroups);
                            this.expertQuery.setValue({
                                filter: expertQueryModel.filter,
                                searchContexts: expertQueryModel.searchContexts.map(x => x.value)
                            });
                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        } else {
            const io: ConfirmDialogModel = {
                inputs: {
                    heading: 'Monitoring.ExpertBooleanSearch.SwitchToExpertMode.header',
                    message: 'Monitoring.ExpertBooleanSearch.SwitchToExpertMode.blocked.message',
                    okLabel: 'Monitoring.ExpertBooleanSearch.SwitchToExpertMode.acknowledge',
                    showCancel: false
                },
                outputs: {
                    dialogClose: () => {}
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.searchContextsSubscriptions.forEach(sub => sub.unsubscribe());
    }
}

